function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { stableStringify } from "@sail/utils";
import PollingController from "../polling/PollingController";
class ObservableData {
    notifyObservers(result) {
        this.observers.forEach(function triggerObserverNext(observer) {
            observer.next(result);
        });
    }
    notifyObserversWithCurrentResult() {
        var _this_latestResult, _this_latestResult1;
        this.notifyObservers({
            data: ((_this_latestResult = this.latestResult) === null || _this_latestResult === void 0 ? void 0 : _this_latestResult.data) || null,
            errors: ((_this_latestResult1 = this.latestResult) === null || _this_latestResult1 === void 0 ? void 0 : _this_latestResult1.errors) || null
        });
    }
    watch() {
        return this.resultStore.watchResults(this.query, this.variables, (results)=>{
            this.notifyObservers(results);
        });
    }
    setIsPending(isPending) {
        var _this_latestResult;
        if (((_this_latestResult = this.latestResult) === null || _this_latestResult === void 0 ? void 0 : _this_latestResult.isPending) !== isPending) {
            this.latestResult = this.latestResult ? {
                data: this.latestResult.data,
                errors: this.latestResult.errors,
                isPending
            } : {
                data: null,
                errors: null,
                isPending
            };
            this.notifyObserversWithCurrentResult();
        }
    }
    getCurrentResult() {
        var _this_latestResult, _this_latestResult1;
        const newResult = this.resultStore.getCurrentResult(this.query, this.variables);
        // Adapt the value for new results from the result store
        if (stableStringify((_this_latestResult = this.latestResult) === null || _this_latestResult === void 0 ? void 0 : _this_latestResult.data) !== stableStringify(newResult.data) || stableStringify((_this_latestResult1 = this.latestResult) === null || _this_latestResult1 === void 0 ? void 0 : _this_latestResult1.errors) !== stableStringify(newResult.errors)) {
            var _this_latestResult2;
            this.latestResult = {
                data: newResult.data,
                errors: newResult.errors,
                isPending: ((_this_latestResult2 = this.latestResult) === null || _this_latestResult2 === void 0 ? void 0 : _this_latestResult2.isPending) || false
            };
        }
        // Return a stable reference if the results haven't changed.
        return this.latestResult;
    }
    isActive() {
        return this.observers.size > 0;
    }
    registerPollConfig(observer, pollConfig) {
        var _this_pollingController;
        (_this_pollingController = this.pollingController) === null || _this_pollingController === void 0 ? void 0 : _this_pollingController.registerConfig(observer, pollConfig);
    }
    unregisterPollConfig(observer) {
        var _this_pollingController;
        (_this_pollingController = this.pollingController) === null || _this_pollingController === void 0 ? void 0 : _this_pollingController.unregisterConfig(observer);
    }
    getEffectiveInterval() {
        var _this_pollingController;
        var _this_pollingController_getEffectiveInterval;
        return (_this_pollingController_getEffectiveInterval = (_this_pollingController = this.pollingController) === null || _this_pollingController === void 0 ? void 0 : _this_pollingController.getEffectiveInterval()) !== null && _this_pollingController_getEffectiveInterval !== void 0 ? _this_pollingController_getEffectiveInterval : null;
    }
    getEffectiveExpectedErrors() {
        var _this_pollingController;
        var _this_pollingController_getEffectiveExpectedErrors;
        return (_this_pollingController_getEffectiveExpectedErrors = (_this_pollingController = this.pollingController) === null || _this_pollingController === void 0 ? void 0 : _this_pollingController.getEffectiveExpectedErrors()) !== null && _this_pollingController_getEffectiveExpectedErrors !== void 0 ? _this_pollingController_getEffectiveExpectedErrors : [];
    }
    getEffectiveRetryConfig() {
        var _this_pollingController;
        var _this_pollingController_getEffectiveRetryConfig;
        return (_this_pollingController_getEffectiveRetryConfig = (_this_pollingController = this.pollingController) === null || _this_pollingController === void 0 ? void 0 : _this_pollingController.getEffectiveRetryConfig()) !== null && _this_pollingController_getEffectiveRetryConfig !== void 0 ? _this_pollingController_getEffectiveRetryConfig : null;
    }
    resetPollTimer() {
        var _this_pollingController;
        (_this_pollingController = this.pollingController) === null || _this_pollingController === void 0 ? void 0 : _this_pollingController.resetTimer();
    }
    handleFetchResultForPolling(errors) {
        if (!this.pollingController) {
            return;
        }
        var _errors_clientErrors_length;
        const hasClientErrors = ((_errors_clientErrors_length = errors === null || errors === void 0 ? void 0 : errors.clientErrors.length) !== null && _errors_clientErrors_length !== void 0 ? _errors_clientErrors_length : 0) > 0;
        const hasUnexpectedErrors = errors ? this.hasUnexpectedErrors(errors) : false;
        this.pollingController.handleFetchResult({
            hasClientErrors,
            hasUnexpectedErrors
        });
    }
    hasUnexpectedErrors(errors) {
        if (errors.networkErrors.length > 0) {
            return true;
        }
        const expectedErrors = this.getEffectiveExpectedErrors();
        for (const gqlError of errors.graphQlErrors){
            var _gqlError_extensions_error, _gqlError_extensions;
            const errorCode = (_gqlError_extensions = gqlError.extensions) === null || _gqlError_extensions === void 0 ? void 0 : (_gqlError_extensions_error = _gqlError_extensions.error) === null || _gqlError_extensions_error === void 0 ? void 0 : _gqlError_extensions_error.code;
            if (errorCode && !expectedErrors.includes(errorCode)) {
                return true;
            }
        }
        return false;
    }
    constructor(query, variables, resultStore, pollingClock = null){
        // Document node that will be used to check apollo cache for data.
        _define_property(this, "query", void 0);
        // Variables given to the useQuery hook, used to check apollo cache for data.
        _define_property(this, "variables", void 0);
        // List of observers for this particular document + variables combination.
        _define_property(this, "observers", new Set());
        _define_property(this, "resultStore", void 0);
        _define_property(this, "latestResult", {
            data: null,
            errors: null,
            isPending: false
        });
        _define_property(this, "pollingController", null);
        this.query = query;
        this.variables = variables;
        this.resultStore = resultStore;
        if (pollingClock) {
            this.pollingController = new PollingController(pollingClock, {
                isCurrentlyFetching: ()=>this.isCurrentlyFetching(),
                triggerRefetch: ()=>this.triggerRefetch()
            });
        }
    }
}
export { ObservableData as default };

//# sourceMappingURL=ObservableData.js.map